import { api } from "./generatedApi";
import { auth } from "./generatedAuth";

const tagTypes = [
  "devices",
  "subjects",
  "users",
  "notificationHandlers",
  "rules",
  "zones"
] as const;

const LIST_ID = "LIST" as const;

type TagType = (typeof tagTypes)[number];

const invalidateId = (id: string, tagType: TagType) => {
  return [
    { id, type: tagType },
    { id: LIST_ID, type: tagType }
  ] as const;
};

const provideId = (id: string, tagType: TagType) => {
  return [{ id, type: tagType }] as const;
};

const listTag = (tagType: TagType) => {
  return [{ id: LIST_ID, type: tagType }] as const;
};

const enhancedApi = api.enhanceEndpoints({
  addTagTypes: tagTypes,
  endpoints: {
    createSubjectSubjectsPost: {
      invalidatesTags: listTag("subjects")
    },
    createUserUsersPost: {
      invalidatesTags: listTag("users")
    },
    createNotificationHandlerNotificationHandlersPost: {
      invalidatesTags: listTag("notificationHandlers")
    },
    createRuleRulesPost: {
      invalidatesTags: listTag("rules")
    },
    createZoneZonesPost: {
      invalidatesTags: listTag("zones")
    },
    updateDeviceDevicesIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "devices")
    },
    updateSubjectSubjectsIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "subjects")
    },
    updateUserUsersIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "users")
    },
    updateNotificationHandlerNotificationHandlersIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "notificationHandlers")
    },
    updateRuleRulesIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "rules")
    },
    updateZoneZonesIdPatch: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "zones")
    },
    deleteDeviceDevicesIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "devices")
    },
    deleteSubjectSubjectsIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "subjects")
    },
    deleteUserUsersIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "users")
    },
    deleteNotificationHandlerNotificationHandlersIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "notificationHandlers")
    },
    deleteRuleRulesIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "rules")
    },
    deleteZoneZonesIdDelete: {
      invalidatesTags: (_, __, { id }) => invalidateId(id, "zones")
    },
    findDeviceByIdDevicesIdGet: {
      providesTags: (_, __, { id }) => provideId(id, "devices")
    },
    findSubjectByIdSubjectsIdGet: {
      providesTags: (_, __, { id }) => provideId(id, "subjects")
    },
    findUserByIdUsersIdGet: {
      providesTags: (_, __, { id }) => provideId(id, "users")
    },
    findZoneByIdZonesIdGet: {
      providesTags: (_, __, { id }) => provideId(id, "zones")
    },
    listDevicesDevicesGet: {
      providesTags: listTag("devices")
    },
    listSubjectsSubjectsGet: {
      providesTags: listTag("subjects")
    },
    listUsersUsersGet: {
      providesTags: listTag("users")
    },
    listNotificationHandlersNotificationHandlersGet: {
      providesTags: listTag("notificationHandlers")
    },
    listRulesRulesGet: {
      providesTags: listTag("rules")
    },
    listZonesZonesGet: {
      providesTags: listTag("zones")
    }
  }
});

export { enhancedApi as api };

export const {
  useFindTenantByIdTenantsIdGetQuery,
  useListZonesZonesGetQuery,
  useCreateZoneZonesPostMutation,
  useListZoneTreeZonesTreeGetQuery,
  useFindZoneByIdZonesIdGetQuery,
  useDeleteZoneZonesIdDeleteMutation,
  useUpdateZoneZonesIdPatchMutation,
  useZoneParentZonesIdParentGetQuery,
  useListZoneChildrenZonesIdChildrenGetQuery,
  useListZoneDescendantsZonesIdDescendantsGetQuery,
  useListZoneAncestorsZonesIdAncestorsGetQuery,
  useZoneTenantZonesIdTenantGetQuery,
  useListSubjectsSubjectsGetQuery,
  useCreateSubjectSubjectsPostMutation,
  useFindSubjectByIdSubjectsIdGetQuery,
  useDeleteSubjectSubjectsIdDeleteMutation,
  useUpdateSubjectSubjectsIdPatchMutation,
  useListDevicesDevicesGetQuery,
  useRegisterOrCreateADeviceDevicesPostMutation,
  useFindDeviceByIdDevicesIdGetQuery,
  useDeleteDeviceDevicesIdDeleteMutation,
  useUpdateDeviceDevicesIdPatchMutation,
  useListUsersUsersGetQuery,
  useCreateUserUsersPostMutation,
  useFindUserByIdUsersIdGetQuery,
  useDeleteUserUsersIdDeleteMutation,
  useUpdateUserUsersIdPatchMutation,
  useResetPasswordUsersIdResetPasswordPostMutation,
  useListUserZonesUsersIdZonesGetQuery,
  useAddUserZoneAssociationUsersUserIdZonesZoneIdPostMutation,
  useRemoveUserZoneAssociationUsersUserIdZonesZoneIdDeleteMutation,
  useLiveMonitoringLiveMonitoringGetQuery,
  useGenerateReportsReportsGetQuery,
  useListSessionsSessionsGetQuery,
  useFindSessionSessionsSessionIdGetQuery,
  useListNotificationHandlersNotificationHandlersGetQuery,
  useCreateNotificationHandlerNotificationHandlersPostMutation,
  useFindNotificationHandlerNotificationHandlersIdGetQuery,
  useDeleteNotificationHandlerNotificationHandlersIdDeleteMutation,
  useUpdateNotificationHandlerNotificationHandlersIdPatchMutation,
  useTestNotificationHandlerNotificationHandlersIdTestPostMutation,
  useListRulesRulesGetQuery,
  useCreateRuleRulesPostMutation,
  useListRulePresetsRulesPresetsGetQuery,
  useFindRuleRulesIdGetQuery,
  useDeleteRuleRulesIdDeleteMutation,
  useUpdateRuleRulesIdPatchMutation,
  useListLogsNotificationLogsGetQuery,
} = enhancedApi;

const enhancedAuth = auth.enhanceEndpoints({
  addTagTypes: ["authUser"],
  endpoints: {
    getAuthenticatedUserUserGet: {
      providesTags: ["authUser"]
    },
    updateAuthenticatedUserUserPatch: {
      invalidatesTags: ["authUser"]
    }
  }
});

export { enhancedAuth as auth };

export const {
  useGetAuthenticatedUserUserGetQuery,
  useLazyGetAuthenticatedUserUserGetQuery,
  useResetPasswordUserResetPasswordPostMutation,
  useUpdateAuthenticatedUserUserPatchMutation,
  useLazyListTenantsTenantsGetQuery
} = enhancedAuth;
